import { useEffect, useState } from 'react';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { BufferedEventBus } from '../utils/BufferedEventBus';
import { APP_FOREGROUNDED } from '../AppContainer';
import { SocketState } from './SocketState';
import { isAutomatedTests } from '../utils/isAutomatedTests';

/**
 * E2E-only affordances for the realtime note specs. Mounted globally in InnerhiveApp;
 * renders nothing for real users and a couple of off-screen buttons for automated tests, so the
 * test can drive them via the AppDriver rather than through window.IOC, whose container is only
 * fully configured on the app shell. Not shipped to real users: isAutomatedTests() is only true when the E2E harness
 * sets the IS_AUTOMATED_TESTS localStorage flag (see wdio.common.conf.ts).
 *
 * - test-emit-app-foregrounded: emits APP_FOREGROUNDED on the BufferedEventBus, the exact signal a
 *   real native foreground produces. Exercises both subscribers — SocketState rebuilds the socket
 *   and ForegroundReconciler runs the CareMapState.reload + NotesModel.reconcile catch-up.
 * - test-close-socket: closes the realtime websocket (preventing retry) so a co-owner's change is NOT
 *   pushed in realtime, isolating the foreground catch-up path under test.
 * - test-reconcile-happened: its data-reconciled attribute turns true once a foreground catch-up has
 *   completed. A test that needs the notes list to stay frozen must wait for the boot-time catch-up
 *   to FINISH before it changes anything server-side, or that catch-up merges the change in behind
 *   it. The reconciler's own in-flight flag is private, and TypeScript's private is compile-time
 *   only, so a test reading it would keep working right up until the field is renamed and then
 *   silently pass while racing again; its completion event is public, so that is surfaced instead.
 *   It is a latch rather than a count because BufferedEventBus replays buffered events to every new
 *   subscriber, so a counting handler would not be idempotent as the bus requires.
 */
export const TestForegroundHook: React.FC = () => {
    const events = useInstance(BufferedEventBus);
    const socketState = useInstance(SocketState);

    const [hasReconciled, setHasReconciled] = useState(false);
    useEffect(() => {
        if (!isAutomatedTests()) {
            return;
        }
        return events.on('ForegroundReconciler.reconciled', () => setHasReconciled(true));
    }, [events]);

    if (!isAutomatedTests()) {
        return null;
    }

    // off-screen but interactable (display:none is not clickable in WebdriverIO); only ever rendered
    // under automated tests anyway
    const hiddenStyle: React.CSSProperties = { position: 'fixed', bottom: 0, left: 0, width: 1, height: 1, opacity: 0, zIndex: -1 };

    return (
        <>
            <button
                data-id="test-emit-app-foregrounded"
                onClick={() => events.emit(APP_FOREGROUNDED)}
                style={hiddenStyle}
            >
                emit foregrounded
            </button>
            <button
                data-id="test-close-socket"
                onClick={() => socketState.close()}
                style={hiddenStyle}
            >
                close socket
            </button>
            <div
                data-id="test-reconcile-happened"
                data-reconciled={String(hasReconciled)}
                style={hiddenStyle}
            />
        </>
    );
};
